import { useEffect, useMemo, useRef, useState } from 'react'

import { RefreshCw } from 'lucide-react'

import { apiGetOrderHistory } from '@src/api/order.api'
import OrderHistoryTableComponent, { OrderItemTableRow } from '@src/components/OrderHistoryTable'
import SearchTokenInput from '@src/components/SearchTokenInput'
import ConnectWallet from '@src/components/buttons/ConnectWallet'
import NoWalletIcon from '@src/components/icons/NoWallet'
import { useWalletConnections } from '@src/hooks/useWalletConnections'
import { IOrderHistoryItem } from '@src/types/order'
import { IToken } from '@src/types/token'
import { useConfigStore } from '@src/zustand_stores'
import { useTokensStore } from '@src/zustand_stores'

const OrdersPage = () => {
    const {
        isEVMConnected,
        evmWalletAddress,
        isSolanaConnected,
        solanaWalletAddress,
        isTronConnected,
        tronWalletAddress,
    } = useWalletConnections()

    const availableNetworks = useConfigStore(config => config.availableNetworks)
    const { tokens: zustandTokens } = useTokensStore()

    const [search, setSearch] = useState('')
    const [orderHistoryTableData, setOrderHistoryTableData] = useState<OrderItemTableRow[]>([])
    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [walletsFullyLoaded, setWalletsFullyLoaded] = useState<boolean>(false)
    const [isRefreshing, setIsRefreshing] = useState<boolean>(false)
    const previousWallets = useRef<string[]>([])

    const connectedWallets = useMemo(() => {
        const wallets = []
        if (isEVMConnected && evmWalletAddress) wallets.push(evmWalletAddress)
        if (isSolanaConnected && solanaWalletAddress) wallets.push(solanaWalletAddress)
        if (isTronConnected && tronWalletAddress) wallets.push(tronWalletAddress)
        return wallets
    }, [isEVMConnected, evmWalletAddress, isSolanaConnected, solanaWalletAddress, isTronConnected, tronWalletAddress])

    // Check if all expected wallets are loaded
    useEffect(() => {
        // If no wallets are connected, we consider them fully loaded
        if (!isEVMConnected && !isSolanaConnected && !isTronConnected) {
            setWalletsFullyLoaded(true)
            return
        }

        // Check if all connected wallet types have their addresses loaded
        const evmReady = !isEVMConnected || (isEVMConnected && evmWalletAddress)
        const solanaReady = !isSolanaConnected || (isSolanaConnected && solanaWalletAddress)
        const tronReady = !isTronConnected || (isTronConnected && tronWalletAddress)

        const allWalletsReady = evmReady && solanaReady && tronReady

        if (allWalletsReady) {
            setWalletsFullyLoaded(true)
        } else {
            setWalletsFullyLoaded(false)
        }
    }, [isEVMConnected, evmWalletAddress, isSolanaConnected, solanaWalletAddress, isTronConnected, tronWalletAddress])

    const isWalletConnected = connectedWallets.length > 0

    const getChainIdFromName = (name: string) => {
        // Normalize the name by removing spaces and converting to lowercase
        const normalizedName = name.toLowerCase().replace(/\s+/g, '')

        return Object.keys(availableNetworks).find((chainId: string) => {
            const networkName = availableNetworks[chainId].name.toLowerCase().replace(/\s+/g, '')
            return networkName === normalizedName
        })
    }

    const getTokenFromAddress = (address: string) => {
        try {
            return zustandTokens.find((token: IToken) => token.address?.toLowerCase() === address.toLowerCase())
        } catch (error) {
            console.error('Error in getTokenFromAddress:', error)
            return null
        }
    }

    const convertToTableRows = (data: IOrderHistoryItem[], walletAddress: string) => {
        const orderTableRows: OrderItemTableRow[] = []

        for (const orderItem of data) {
            const sellToken = getTokenFromAddress(orderItem.src_asset_address)
            const buyToken = getTokenFromAddress(orderItem.dst_asset_address)
            const originChainId = getChainIdFromName(orderItem.src_chain)
            const destChainId = getChainIdFromName(orderItem.dst_chain)

            if (!sellToken || !buyToken || !originChainId || !destChainId) {
                continue
            }

            const tableRow: OrderItemTableRow = {
                sellToken,
                buyToken,
                sellQuantity: orderItem.src_amount,
                buyQuantity: orderItem.dst_amount,
                originChainId,
                destChainId,
                originChainTxHash: orderItem.place_tx,
                destChainTxHash: orderItem.fill_tx,
                created_at: orderItem.created_at,
                filled_at: orderItem.filled_at,
                expires_at: orderItem.expires_at,
                completed: orderItem.completed,
                eta: orderItem.eta,
                order_type: orderItem.order_type as 'order_type' | 'cctp',
                walletAddress,
            }
            orderTableRows.push(tableRow)
        }

        return orderTableRows
    }

    const getOrderHistory = async () => {
        // Clear existing data and show loading state
        setIsLoading(true)
        setIsRefreshing(true)
        setOrderHistoryTableData([])

        try {
            const allOrderPromises = connectedWallets.map(async wallet => {
                try {
                    const data = await apiGetOrderHistory(wallet)
                    return convertToTableRows(data || [], wallet)
                } catch (err) {
                    console.error(`Error fetching orders for wallet ${wallet}:`, err)
                    return []
                }
            })

            const allOrders = await Promise.all(allOrderPromises)
            const flattenedOrders = allOrders.flat()

            // Sort by created_at date, most recent first
            const sortedOrders = flattenedOrders.sort((a, b) => {
                return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
            })

            setOrderHistoryTableData(sortedOrders)
        } catch (err) {
            console.error('Error fetching orders:', err)
            setOrderHistoryTableData([])
        } finally {
            setIsLoading(false)
            setIsRefreshing(false)
        }
    }

    const handleManualRefresh = () => {
        if (isWalletConnected && walletsFullyLoaded && !isRefreshing) {
            getOrderHistory()
        }
    }

    // Check if wallets have changed to refresh data
    useEffect(() => {
        const currentWallets = connectedWallets.join(',')
        const prevWallets = previousWallets.current.join(',')

        // If wallets changed, update the reference
        if (currentWallets !== prevWallets) {
            previousWallets.current = [...connectedWallets]

            // If we have wallets and they're fully loaded, fetch data
            if (isWalletConnected && walletsFullyLoaded) {
                getOrderHistory()
            } else if (!isWalletConnected) {
                // Reset data if no wallets connected
                setOrderHistoryTableData([])
                setIsLoading(false)
            }
        }
    }, [connectedWallets.join(','), walletsFullyLoaded])

    // Fetch data when wallets are fully loaded
    useEffect(() => {
        if (isWalletConnected && walletsFullyLoaded) {
            getOrderHistory()
        }
    }, [walletsFullyLoaded])

    const searchedOrderHistory = useMemo(() => {
        if (!search) {
            return orderHistoryTableData
        }
        const searchLowerCase = search.toLowerCase()
        const filteredResults = orderHistoryTableData.filter(
            order =>
                availableNetworks[order.originChainId]?.name?.toLowerCase()?.includes(searchLowerCase) ||
                availableNetworks[order.destChainId]?.name?.toLowerCase()?.includes(searchLowerCase) ||
                order.originChainTxHash.toLowerCase().includes(search) ||
                order.destChainTxHash?.toLowerCase().includes(search) ||
                order.buyToken.name.toLowerCase().includes(searchLowerCase) ||
                order.sellToken.name.toLowerCase().includes(searchLowerCase) ||
                order.walletAddress?.toLowerCase().includes(searchLowerCase)
        )
        return filteredResults
    }, [search, orderHistoryTableData])

    // Memoize the order counts to avoid recalculating on every render
    const orderCounts = useMemo(() => {
        const counts = {
            pending: 0,
            expired: 0,
        }

        const currentTime = new Date().getTime()

        orderHistoryTableData.forEach(item => {
            // Skip completed orders
            if (item.destChainTxHash) return

            const expirationTime = item.expires_at ? new Date(item.expires_at).getTime() : 0

            if (expirationTime && currentTime > expirationTime) {
                counts.expired++
            } else {
                counts.pending++
            }
        })

        return counts
    }, [orderHistoryTableData])

    return (
        <>
            {!isWalletConnected ? (
                <div className='container flex flex-grow flex-col items-center justify-center max-mobile-custom:px-4'>
                    <div className='flex flex-col items-center justify-center space-y-[21px]'>
                        <NoWalletIcon />
                        <div className='space-y-3 text-center'>
                            <p className='text-xl font-semibold'>No wallet connected</p>
                            <span className='text-xs font-medium'>Connect a wallet to see order history</span>
                        </div>
                        <ConnectWallet />
                    </div>
                </div>
            ) : (
                <>
                    <div className='container flex flex-col space-y-7 py-5 max-mobile-custom:px-4 md:py-7'>
                        <div className='flex flex-col space-y-6 md:space-y-8'>
                            <div className='flex flex-col space-y-1'>
                                <span className='text-sm text-gray-400'>Connected Addresses</span>
                                <div className='flex flex-col space-y-2'>
                                    {connectedWallets.map(wallet => (
                                        <div key={wallet} className='no-scrollbar overflow-x-auto md:overflow-visible'>
                                            <span className='whitespace-nowrap font-mono text-base font-bold text-white md:break-all md:text-2xl'>
                                                {wallet}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            {/* Stats section - horizontal cards on mobile */}
                            <div className='no-scrollbar flex flex-row gap-4 overflow-x-auto pb-2 sm:grid sm:grid-cols-3 sm:gap-6 sm:pb-0 md:gap-8'>
                                {/* Total Orders */}
                                <div className='flex w-[160px] flex-shrink-0 flex-col rounded-lg bg-gray-800/30 p-3 sm:w-auto'>
                                    <span className='text-sm text-gray-400'>Total Orders</span>
                                    <div className='flex items-baseline gap-2'>
                                        <span className='text-2xl font-medium leading-none text-white md:text-[32px] lg:text-[40px]'>
                                            {isLoading ? (
                                                <div className='mt-2 h-6 w-16 animate-pulse rounded bg-gray-400 md:h-8 md:w-24' />
                                            ) : (
                                                orderHistoryTableData.length || 0
                                            )}
                                        </span>
                                    </div>
                                </div>

                                {/* Pending Orders */}
                                <div className='flex w-[160px] flex-shrink-0 flex-col rounded-lg bg-gray-800/30 p-3 sm:w-auto'>
                                    <span className='text-sm text-gray-400'>Pending Orders</span>
                                    <div className='flex items-baseline gap-2'>
                                        <span className='text-2xl font-medium leading-none text-white md:text-[32px] lg:text-[40px]'>
                                            {isLoading ? (
                                                <div className='mt-2 h-6 w-16 animate-pulse rounded bg-gray-400 md:h-8 md:w-24' />
                                            ) : (
                                                orderCounts.pending
                                            )}
                                        </span>
                                    </div>
                                </div>

                                {/* Expired Orders */}
                                <div className='flex w-[160px] flex-shrink-0 flex-col rounded-lg bg-gray-800/30 p-3 sm:w-auto'>
                                    <span className='text-sm text-gray-400'>Expired Orders</span>
                                    <div className='flex items-baseline gap-2'>
                                        <span className='text-2xl font-medium leading-none text-white md:text-[32px] lg:text-[40px]'>
                                            {isLoading ? (
                                                <div className='mt-2 h-6 w-16 animate-pulse rounded bg-gray-400 md:h-8 md:w-24' />
                                            ) : (
                                                orderCounts.expired
                                            )}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className='flex flex-row items-center justify-between gap-3'>
                            <div className='relative w-full sm:ml-auto sm:w-auto'>
                                <SearchTokenInput
                                    className='w-full pr-12 sm:w-[300px] md:w-[350px]'
                                    onValueChange={val => setSearch(val)}
                                />
                                <div className='absolute right-2 top-1/2 flex -translate-y-1/2 items-center'>
                                    {isRefreshing && <span className='mr-2 text-xs text-gray-400'>Refreshing...</span>}
                                    <button
                                        onClick={handleManualRefresh}
                                        disabled={isRefreshing || !walletsFullyLoaded}
                                        className='flex items-center justify-center rounded-full p-1.5 transition-colors hover:bg-gray-700/50 disabled:cursor-not-allowed disabled:opacity-50'
                                        title='Refresh order history'
                                    >
                                        <RefreshCw
                                            size={20}
                                            className={`text-white ${isRefreshing ? 'animate-spin' : ''}`}
                                        />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='container flex flex-grow flex-col items-center max-mobile-custom:px-4'>
                        <OrderHistoryTableComponent
                            data={searchedOrderHistory}
                            loadingStatus={isLoading}
                            search={search}
                        />
                    </div>
                </>
            )}
        </>
    )
}

export default OrdersPage
